import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { getEnvironmentProps } from "../utils/environment";
import { listenerMiddleware } from "./listenerMiddleware";
import { registerLoadingListeners } from "./listeners/loadingListeners";
import { registerSessionListeners } from "./listeners/sessionListeners";
import diaryReducer from "./slices/diarySlice";
import { registerConsentListeners } from "./listeners/consentListeners";
import { registerDeviceListeners } from "./listeners/deviceListeners";
import { registerFeatureFlagsListeners } from "./listeners/featureFlagsListeners";
import customerReducer, {
  selectedCustomerChanged
} from "./slices/customerSlice";
import consentReducer from "./slices/consentSlice";
import deviceReducer from "./slices/deviceSlice";
import featureFlagsReducer from "./slices/featureFlagsSlice";
import loadingReducer from "./slices/loadingSlice";
import modalReducer from "./slices/modalSlice";
import sessionReducer from "./slices/sessionSlice";
import spaceOptimiserReducer from "./slices/spaceOptimiserSlice";
import uiReducer from "./slices/uiSlice";

const rootReducer = combineReducers({
  ui: uiReducer,
  session: sessionReducer,
  customer: customerReducer,
  loading: loadingReducer,
  modal: modalReducer,
  spaceOptimiser: spaceOptimiserReducer,
  device: deviceReducer,
  consent: consentReducer,
  featureFlags: featureFlagsReducer,
  diary: diaryReducer
});

export type RootState = ReturnType<typeof rootReducer>;

registerSessionListeners();
registerLoadingListeners();
registerDeviceListeners();
registerConsentListeners();
registerFeatureFlagsListeners();

// DevTools expose the whole state (user PII, the Intercom JWT) to browser extensions, so they are
// only on in local development and hosted QA, which resolves to "Pre-Prod". Every other environment,
// including Staging and anything unknown, gets none. The store is created before a token exists.
const REDUX_DEVTOOLS_ENVIRONMENTS: ReadonlySet<string> = new Set([
  "Development",
  "Pre-Prod"
]);

export const isReduxDevToolsEnabled = (): boolean =>
  REDUX_DEVTOOLS_ENVIRONMENTS.has(getEnvironmentProps(null).ENVIRONMENT);

/** `ViewBooking` fields the SDK deserialises as `Date`; only these are exempt. */
export const SELECTED_BOOKING_DATE_FIELDS = [
  "date",
  "lastUpdated",
  "createdDate",
  "followUp",
  "statusChangedDate"
] as const;

/** Nested `ViewBooking` paths that hold a `Date`; `<n>` is the array index. */
export const SELECTED_BOOKING_NESTED_DATE_PATHS = [
  "deposits.<n>.date",
  "refunds.<n>.createdDate",
  "preorders.<n>.created_date"
] as const;

const escapeRegExp = (value: string): string =>
  value.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);

const nestedDatePattern = SELECTED_BOOKING_NESTED_DATE_PATHS.map((path) =>
  path
    .split(".<n>.")
    .map(escapeRegExp)
    .join(String.raw`\.\d+\.`)
).join("|");

const selectedBookingDatePaths = (prefix: string): (string | RegExp)[] => [
  ...SELECTED_BOOKING_DATE_FIELDS.map((field) => `${prefix}.${field}`),
  new RegExp(`^${escapeRegExp(prefix)}\\.(?:${nestedDatePattern})$`)
];

export const makeStore = (preloadedState?: Partial<RootState>) =>
  configureStore({
    reducer: rootReducer,
    preloadedState,
    devTools: isReduxDevToolsEnabled(),
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        // Customer API models intentionally retain their Date fields.
        serializableCheck: {
          ignoredPaths: [
            "customer.selectedCustomer",
            ...selectedBookingDatePaths("diary.selectedBooking")
          ],
          ignoredActions: [selectedCustomerChanged.type],
          ignoredActionPaths: [
            "meta.arg",
            "meta.baseQueryMeta",
            ...selectedBookingDatePaths("payload"),
            ...selectedBookingDatePaths("payload.changes"),
            ...selectedBookingDatePaths("payload.selectedBooking")
          ]
        }
      }).prepend(listenerMiddleware.middleware)
  });

export type AppStore = ReturnType<typeof makeStore>;
export type AppDispatch = AppStore["dispatch"];

export const store = makeStore();
